import React from 'react';
import InputLabel from '@material-ui/core/InputLabel';
import Checkbox from '@material-ui/core/Checkbox';
import Grid from '@material-ui/core/Grid';
import FormGroup from '@material-ui/core/FormGroup';
import FormControlLabel from '@material-ui/core/FormControlLabel';
import Link from '@material-ui/core/Link';
import MenuItem from '@material-ui/core/MenuItem';
import Select from '@material-ui/core/Select';

import { Table, TableColumn } from '@backstage/core-components';

import { TestResult } from 'plugin-test-results-common';

import { DAY_WINDOWS } from './config';
import { getRelativeTime } from './util/relative-time';
import { TestValue, TestCollation, CollatedTestResults } from './util/types';
import { getDeepLink } from './util/links';
import { Trend } from './Trend';

const DEFAULT_TREND_COUNT = 16;

function getFailureRate(testValue: TestValue): number {
  const numFailures = testValue.results.filter(
    r => r.status === 'failed',
  ).length;
  const rate = Math.round((100 * numFailures) / testValue.results.length);
  return rate;
}

function averageDurationMs(testValue: TestValue): number {
  const sum = testValue.results.reduce((t, r) => t + r.durationMs, 0);
  return Math.round(sum / testValue.results.length);
}

function formatMilliseconds(avgMs: number): string {
  const milliseconds = avgMs;
  const seconds = milliseconds / 1000;
  const minutes = seconds / 60;
  const hours = minutes / 60;

  if (milliseconds < 1000) return `${milliseconds} ms`;
  else if (seconds < 60) return `${seconds.toFixed(1)} seconds`;
  else if (minutes < 60) return `${minutes.toFixed(1)} minutes`;
  return `${hours.toFixed(1)} hours`;
}

function collateTestResults(
  testResults: TestResult[],
  selectedBranch: string,
  onlyFailures: boolean,
): CollatedTestResults {
  const initCollation: TestCollation = {};
  const availableBranches: Set<string> = new Set();
  let commonPrefix = '';
  let commonPrefixSet = false;
  const collation = testResults.reduce(
    (coll: TestCollation, result: TestResult) => {
      availableBranches.add(result.buildBranch);
      if (result.buildBranch !== selectedBranch) {
        return coll;
      }
      if (onlyFailures && result.status !== 'failed') {
        return coll;
      }
      const { fullName } = result;
      const idName = fullName || result.name;
      if (!idName) {
        return coll;
      }
      if (!coll.hasOwnProperty(idName)) {
        coll[idName] = { fullName: idName, results: [result] };

        // Common prefix calculation
        if (fullName === idName && fullName.includes('.')) {
          if (!commonPrefixSet) {
            commonPrefix = fullName;
            commonPrefixSet = true;
          } else {
            for (let i = 0; i < fullName.length; i++) {
              if (i >= commonPrefix.length) {
                break;
              }
              if (fullName.charAt(i) !== commonPrefix.charAt(i)) {
                commonPrefix = commonPrefix.slice(0, i);
                break;
              }
            }
          }
        }
      } else {
        coll[idName].results.push(result);
      }
      return coll;
    },
    initCollation,
  );
  return { availableBranches, collation, commonPrefix };
}

function defineColumns(onlyFailures: boolean): TableColumn<TestEntry>[] {
  const now = +new Date();
  const columns: TableColumn<TestEntry>[] = [
    {
      title: 'Name',
      field: 'trimmedName',
      width: '20em',
      searchable: true,
      render: (rowData: TestEntry) => (
        <span title={rowData.fullName}>{rowData.trimmedName}</span>
      ),
    },
    {
      title: onlyFailures ? 'Last Failure' : 'Last Run',
      field: 'startedAt',
      render: (rowData: TestEntry) => (
        <span title={rowData.startedAt}>
          {getRelativeTime(
            +new Date(rowData.testValue.results[0].startedAt),
            now,
          )}
        </span>
      ),
      width: '10em',
    },
    {
      title: 'Avg Duration',
      field: 'avgDurationMs',
      render: (testEntry: TestEntry) =>
        formatMilliseconds(testEntry.avgDurationMs),
      width: '12em',
      type: 'numeric',
    },
  ];
  if (!onlyFailures) {
    columns.push({
      title: 'Fail Rate',
      field: 'failureRate',
      width: '12em',
      render: (testEntry: TestEntry) => `${testEntry.failureRate}%`,
      type: 'numeric',
      defaultSort: 'desc',
    });
    columns.push({
      title: 'Last Result',
      field: 'status',
      render: (testEntry: TestEntry) => (
        <Link href={getDeepLink(testEntry.testValue.results[0])}>
          {testEntry.testValue.results[0].status}
        </Link>
      ),
      width: '10em',
    });
  }
  columns.push({
    title: onlyFailures ? 'Recent Failures' : 'Trend',
    sorting: false,
    field: 'status',
    render: (testEntry: TestEntry) => (
      <Trend testValue={testEntry.testValue} count={DEFAULT_TREND_COUNT} />
    ),
  });
  return columns;
}

interface TestEntry {
  avgDurationMs: number;
  failureRate: number;
  status: string;
  startedAt: string;
  testValue: TestValue;
  fullName: string;
  trimmedName: string;
}

export type TableControlProps = {
  days: number;
  setDays: (val: number) => void;
  collection: string;
  setCollection: (val: string) => void;
  branch: string;
  setBranch: (val: string) => void;
  onlyFailures: boolean;
  setOnlyFailures: (val: boolean) => void;
  availableCollections: Set<string>;
};

export const TableControl = ({
  days,
  setDays,
  collection,
  setCollection,
  branch,
  setBranch,
  onlyFailures,
  setOnlyFailures,
  availableBranches,
  availableCollections,
}: TableControlProps & { availableBranches: Set<string> }) => {
  type SelectChangeEvent = React.ChangeEvent<{
    name?: string | undefined;
    value: unknown;
  }>;
  type CheckboxChangeEvent = React.ChangeEvent<HTMLInputElement>;
  const selectCollection = (event: SelectChangeEvent) => {
    if (typeof event.target.value === 'string') {
      setCollection(event.target.value);
    }
  };
  const selectBranch = (event: SelectChangeEvent) => {
    if (typeof event.target.value === 'string') {
      setBranch(event.target.value);
    }
  };
  const selectDays = (event: SelectChangeEvent) => {
    if (typeof event.target.value === 'string') {
      setDays(parseInt(event.target.value, 10));
    }
  };
  const changeOnlyFailures = (event: CheckboxChangeEvent) => {
    setOnlyFailures(!!event.target.checked);
  };

  return (
    <Grid container spacing={1} alignItems="center">
      <Grid item xs={3}>
        <InputLabel id="collections-label">Test Collection</InputLabel>
        <Select
          id="collections"
          labelId="collections-label"
          onChange={selectCollection}
          value={collection}
        >
          {Array.from(availableCollections).map(coll => (
            <MenuItem value={coll}>{coll}</MenuItem>
          ))}
        </Select>
      </Grid>
      <Grid item xs={3} key="time-window">
        <InputLabel id="days-label">Time Window</InputLabel>
        <Select
          id="days"
          labelId="days-label"
          onChange={selectDays}
          value={String(days)}
        >
          {DAY_WINDOWS.map(dayValue => (
            <MenuItem value={String(dayValue)}>
              {dayValue === 1 ? 'Last day' : `Last ${dayValue} days`}
            </MenuItem>
          ))}
        </Select>
      </Grid>
      <Grid item xs={3}>
        <InputLabel id="branches-label">Branch</InputLabel>
        <Select
          id="branches"
          labelId="branches-label"
          onChange={selectBranch}
          value={branch}
        >
          {Array.from(availableBranches).map(ab => (
            <MenuItem value={ab}>{ab}</MenuItem>
          ))}
        </Select>
      </Grid>
      <Grid item xs={3} key="only-failures">
        <FormGroup>
          <FormControlLabel
            control={
              <Checkbox
                checked={onlyFailures}
                onChange={changeOnlyFailures}
                name="onlyFailures"
              />
            }
            label="Only Failures"
          />
        </FormGroup>
      </Grid>
    </Grid>
  );
};

export type TestResultsTableProps = {
  testResults: TestResult[];
  control: TableControlProps;
};

export const TestResultsTable = ({
  testResults,
  control,
}: TestResultsTableProps) => {
  const collated = collateTestResults(
    testResults,
    control.branch,
    control.onlyFailures,
  );
  const data: TestEntry[] = Object.values(collated.collation).map(
    testValue => ({
      fullName: testValue.fullName,
      id: testValue.fullName,
      trimmedName: testValue.fullName.startsWith(collated.commonPrefix)
        ? testValue.fullName.slice(collated.commonPrefix.length)
        : testValue.fullName,
      avgDurationMs: averageDurationMs(testValue),
      failureRate: getFailureRate(testValue),
      status: testValue.results[0].status,
      startedAt: testValue.results[0].startedAt,
      trend: <Trend testValue={testValue} count={DEFAULT_TREND_COUNT} />,
      testValue,
    }),
  );
  return (
    <Grid container spacing={1}>
      <Grid item xs={12}>
        <TableControl
          {...control}
          availableBranches={collated.availableBranches}
        />
      </Grid>
      <Grid item xs={12} key="test-results" data-testid="table-parent">
        <Table
          title="Recent Test Results"
          options={{
            search: true,
            padding: 'dense',
            paging: true,
            pageSize: 20,
            pageSizeOptions: [20, 50, 100],
            tableLayout: 'fixed',
          }}
          columns={defineColumns(control.onlyFailures)}
          data={data}
        />
      </Grid>
    </Grid>
  );
};
